/* eslint-disable max-len */
import React, { useContext, useEffect } from 'react';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { Button, Typography } from '@one-thd/sui-atomic-components';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { BackToTop } from '@thd-olt-component-react/back-to-top';
import { Metadata } from '@thd-olt-component-react/metadata';
import classnames from 'classnames';
import { imageBaseUrl } from '../../constants';
import { PrivacyHeader } from './PrivacyHeader';
import { PrivacyMetadata } from './PrivacyMetadata';
import { navigate } from '../../utils/navigationUtils';
import './Home.scss';

export const Home = () => {

  const { channel } = useContext(ExperienceContext);

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('exerciseMyPrivacyRights.static.page-load', {
      pageName: 'privacy and security center',
      pageType: 'content',
      primaryCategory: 'privacy and security center',
      subCategory1: 'privacy and security center',
      subCategory2: 'privacy and security center'
    });
  });

  const metadata = {
    title: 'Privacy & Security Center – The Home Depot',
    description: 'The Privacy & Security Center is designed to help you learn about the information '
      + 'The Home Depot collects and how we use and share it',
    canonical: 'https://www.homedepot.com/privacy/overview'
  };

  const anchorClassname = classnames('white margin-20', { 'privacy-home--anchor-mobile': channel === 'mobile' });

  return (
    <div className="privacy-home">
      <Metadata>
        <PrivacyMetadata
          title={metadata.title}
          description={metadata.description}
          canonical={metadata.canonical}
        />
      </Metadata>
      <div className="container">

        <QueryProvider cacheKey="customer-data-governance">
          <div className="cdg-content--header">
            <div className="sui-grid sui-gap-4">
              <div>
                <Breadcrumbs
                  staticCrumbs={[
                    {
                      url: '/privacy/overview',
                      label: 'Privacy & Security Center'
                    }]}
                />
              </div>
            </div>
          </div>
        </QueryProvider>

        <PrivacyHeader overviewActive />

        <div className="cdg-content--body">
          <div className="white margin-20 sui-grid sui-gap-4 md:sui-grid-cols-12">
            <div className="sui-col-span-12 sui-grid sui-gap-4 md:sui-grid-cols-12">

              {channel === 'mobile'
                && (
                  <div className="sui-col-span-12 md:sui-col-span-6 remove-padding">
                    <img
                      id="mobileHeaderImage"
                      width="720"
                      height="600"
                      alt="Privacy At Homedepot"
                      src={imageBaseUrl + '/privacy-and-security-homepage-hero.jpg'}
                      className="privacy-home__hero-image"
                    />
                  </div>
                )}
              <div className="sui-col-span-12 md:sui-col-span-6 content-summary">
                <Typography variant="h1">Privacy at The Home Depot</Typography>
                <br />
                <p style={{ fontSize: '18px' }}>At The Home Depot, we appreciate that you trust us
                  with your personal information, and we’re committed to using your information in
                  ways that create value for you. Every day, we are guided by our principles,
                  and we work hard to earn and keep your trust.
                </p>
                <br />
                <Typography variant="h4" weight="bold">Our Privacy Principles</Typography>
                <br />
                <b>Transparency</b>
                <p className="principle-verbiage-margins">We&apos;re open and honest with you about
                  how we use and disclose your personal information.
                </p>
                <b>Control</b>
                <p className="principle-verbiage-margins">We give you tools to help you exercise your rights.</p>
                <b>Security</b>
                <p className="principle-verbiage-margins">We use industry-standard means to protect our websites and
                  your information.
                </p>
                <p className="privacy-home__principles-copy">With these principles in mind, the Privacy & Security Center
                  is designed to help you learn about what we do with your data
                  and provide you with tools to take action.
                </p>
                <br />
                <a href="#ToolsToExerciseYourPrivacy" className="privacy-home__cta-link">
                  Explore Our Privacy Tools
                  {/* eslint-disable-next-line @mizdra/layout-shift/require-size-attributes */}
                  <img
                    src={imageBaseUrl + '/caret-orange.svg'}
                    alt=""
                    className="privacy-home__caret"
                  />
                </a>
              </div>

              {channel !== 'mobile'
                && (
                  <div className="sui-col-span-12 md:sui-col-span-6 remove-padding">
                    <img
                      id="defaultHeaderImage"
                      width="720"
                      height="600"
                      alt="Privacy At Homedepot"
                      src={imageBaseUrl + '/privacy-and-security-homepage-hero.jpg'}
                      className="privacy-home__hero-image privacy-home__hero-image--desktop"
                    />
                  </div>
                )}

            </div>
          </div>

          <div className="white margin-20 sui-grid sui-gap-4 md:sui-grid-cols-12">
            <div className="sui-col-span-12">
              <div className="content-title">
                <Typography variant="h3" weight="regular">Understanding Your Data</Typography>
              </div>
            </div>
            <div className={classnames('sui-col-span-12 md:sui-col-span-4 content-summary', { 'privacy-home--content': channel !== 'mobile' })}>
              <img
                height="100"
                width="100"
                alt="Privacy ToolBox"
                src={imageBaseUrl + '/OnlinePrivacy_Icon_InformationCollected.svg'}
                className="privacy-home__card-icon"
              />
              <h3><b>How We Collect Information</b></h3>
              <p>We collect your information when you visit our stores, contact us, or interact with our digital
                offerings. We also collect information from third parties that help us understand your interests
                and preferences better. This allows us to provide the most seamless and personalized shopping experience
                possible.
              </p>
              <br />
              <div className={channel !== 'mobile' ? 'privacy-home--paragraph' : ''}>
                Examples of information collected:
                <ul>
                  <li>Personal information, such as name, email address, and phone number</li>
                  <li>Financial information, such as payment methods</li>
                  <li>Location information</li>
                  <li>Purchase history, including products or services you liked and reviews you submitted</li>
                  <li>Demographic and preference information including interests and other characteristics</li>
                  <li>Internet activity, such as browsing history and search history</li>
                </ul>
                <br />
                <p>
                  We may also collect some information that may be considered sensitive under certain privacy laws,
                  such as account login credentials, race/ethnicity, government identification numbers,
                  biometrics, and precise geo-location.
                </p>
              </div>
              <div className={channel !== 'mobile' ? 'privacy-home--btn' : ''}>
                <Button
                  onClick={() => navigate('/privacy/privacy-and-security-at-a-glance#WhatInformationDoWeCollect')}
                  variant="secondary"
                >Discover More
                </Button>
              </div>
            </div>
            <div className={classnames('sui-col-span-12 md:sui-col-span-4 content-summary', { 'privacy-home--content': channel !== 'mobile' })}>
              <img
                height="100"
                width="100"
                alt="Privacy ToolBox"
                src={imageBaseUrl + '/OnlinePrivacy_Icon_InformationShared.svg'}
                className="privacy-home__card-icon"
              />
              <h3><b>How We Disclose Your Information</b></h3>
              <p>We are selective about who we disclose your information with, and only disclose data
                with select third parties for business or legal reasons. We may disclose information
                that does not directly identify you (such as your device identifier, IP address,
                or other information associated with your online browsing activities) with select
                third parties for their and our advertising and marketing efforts.
              </p>
              <br />
              <div className={channel !== 'mobile' ? 'privacy-home--paragraph' : ''}>
                Examples of who information is disclosed with:
                <ul>
                  <li>Delivery partners</li>
                  <li>Security and payment providers</li>
                  <li>Shipping and mailing vendors</li>
                  <li>Advertising partners</li>
                  <li>Third-party companies</li>
                  <li>Marketing and advertising companies</li>
                </ul>
              </div>
              <div className={channel !== 'mobile' ? 'privacy-home--btn' : ''}>
                <Button
                  onClick={() => navigate('/privacy/privacy-and-security-at-a-glance#HowDoWeDiscloseInformation')}
                  variant="secondary"
                >Explore More
                </Button>
              </div>
            </div>
            <div className={classnames('sui-col-span-12 md:sui-col-span-4 content-summary', { 'privacy-home--content': channel !== 'mobile' })}>
              <img
                height="100"
                width="100"
                alt="Privacy ToolBox"
                src={imageBaseUrl + '/OnlinePrivacy_Icon_InformationUsed.svg'}
                className="privacy-home__card-icon"
              />
              <h3><b>How We Use Your Information</b></h3>
              <p>We use the information you provide us in innovative ways to improve your experience,
                process your information, and keep things running smoothly.
              </p>
              <br />
              <div className={channel !== 'mobile' ? 'privacy-home--paragraph' : ''}>
                Examples of how information is used:
                <ul>
                  <li>Fulfill and deliver orders</li>
                  <li>Process a return</li>
                  <li>Personalize the content in our digital services</li>
                  <li>Recommend relevant products and services</li>
                  <li>Delivering marketing communications and advertising</li>
                  <li>Prevent fraud</li>
                </ul>
              </div>
              <div className={channel !== 'mobile' ? 'privacy-home--btn' : ''}>
                <Button
                  onClick={() => navigate('/privacy/privacy-and-security-at-a-glance#HowDoWeUseInformation')}
                  variant="secondary"
                >Learn More
                </Button>
              </div>
            </div>
          </div>
          <div id="ToolsToExerciseYourPrivacy" className={anchorClassname}>
            <div className="sui-grid sui-gap-4">
              <div className="content-title">
                <Typography variant="h3" weight="regular">Tools to Exercise Your Privacy</Typography>
              </div>
            </div>
            <div className="sui-grid sui-gap-4 md:sui-grid-cols-12">
              <div className="sui-col-span-12 md:sui-col-span-2">
                {/* eslint-disable-next-line @mizdra/layout-shift/require-size-attributes */}
                <img
                  height="100"
                  width="100"
                  alt="Submit Request"
                  src={imageBaseUrl + '/OnlinePrivacy_Icon_SubmitPrivacyRequest.svg'}
                  className="privacy-home__card-icon"
                />
              </div>
              <div className="sui-col-span-12 md:sui-col-span-10 content-summary">
                <h3><b>Submit a Privacy Request</b></h3>
                <p>You may submit a privacy request to:</p>
                <ul>
                  <li>Review, correct, or delete the personal information we collect</li>
                  <li>
                    Opt out of having your personal information sold for valuable consideration
                    or for being shared or used for certain targeted advertising
                  </li>
                  <li>Limit the use of certain sensitive personal information</li>
                </ul>
                <Button
                  onClick={() => navigate('/privacy/Exercise-My-Privacy-Rights')}
                  variant="secondary"
                >Submit Request
                </Button>
              </div>
            </div>
            <div className="sui-grid sui-gap-4 md:sui-grid-cols-12">
              <div className="sui-col-span-12 md:sui-col-span-2">
                {/* eslint-disable-next-line @mizdra/layout-shift/require-size-attributes */}
                <img
                  height="100"
                  width="100"
                  alt="Manage Preferences"
                  src={imageBaseUrl + '/OnlinePrivacy_Icon_ManageMarketingPreferences.svg'}
                  className="privacy-home__card-icon"
                />
              </div>
              <div className="sui-col-span-12 md:sui-col-span-10 content-summary">
                <h3><b>Manage Your Marketing Preferences</b></h3>
                <p>Review and manage how your personal information is used for our direct marketing purposes,
                  such as emails, calls, text messages, direct mail, and catalog mailings.
                </p>
                <Button
                  onClick={() => navigate('/privacy/MyPreferenceCenter')}
                  variant="secondary"
                >Manage Preferences
                </Button>
              </div>
            </div>
          </div>
          <div className="margin-20 sui-grid sui-gap-4" />
          <BackToTop />
        </div>
      </div>
    </div>
  );
};

Home.propTypes = {};

Home.defaultProps = {};

